// Performance utilities for the application
import { config } from '../config';

// Performance monitoring utilities
export const performanceMonitoring = {
  // Measure function execution time
  measureExecution: <T>(fn: () => T, label: string): T => {
    const start = performance.now();
    const result = fn();
    const end = performance.now();
    console.log(`${label}: ${(end - start).toFixed(2)}ms`);
    return result;
  },
  
  // Track bundle size
  trackBundleSize: (bundleName: string, size: number) => {
    console.log(`Bundle ${bundleName}: ${(size / 1024).toFixed(2)}KB`);
  },
  
  // Warn about large bundles
  warnLargeBundle: (bundleName: string, size: number, threshold: number = 244 * 1024) => {
    if (size > threshold) {
      console.warn(`Large bundle detected: ${bundleName} (${(size / 1024).toFixed(2)}KB)`);
    }
  },
};

// Cache utility
export const createCache = <K, V>(maxSize: number = 100) => {
  const cache = new Map<K, V>();
  
  return {
    get: (key: K): V | undefined => cache.get(key),
    set: (key: K, value: V): void => {
      if (cache.size >= maxSize) {
        const firstKey = cache.keys().next().value;
        if (firstKey !== undefined) {
          cache.delete(firstKey);
        }
      }
      cache.set(key, value);
    },
    clear: (): void => cache.clear(),
    size: (): number => cache.size,
  };
};

// Debounce utility
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle utility
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Performance configuration
export const getPerformanceConfig = () => ({
  codeSplitting: {
    enabled: config.performance.enableCodeSplitting,
    chunkSize: 244 * 1024, // 244KB
    maxChunks: 10,
  },
  lazyLoading: {
    enabled: config.performance.enableLazyLoading,
    preloadThreshold: 0.1,
  },
  memoization: {
    enabled: config.performance.enableMemoization,
    maxCacheSize: 100,
    cacheExpiry: 5 * 60 * 1000, // 5 minutes
  },
  bundleAnalysis: {
    enabled: config.performance.bundleAnalyzerEnabled,
    outputPath: 'build/bundle-analyzer-report.html',
  },
});

// Bundle analysis utilities
export const bundleAnalysis = {
  // Analyze bundle size
  analyzeBundle: (bundleName: string, size: number) => {
    const performanceConfig = getPerformanceConfig();
    const sizeKB = (size / 1024).toFixed(2);
    console.log(`Bundle Analysis - ${bundleName}: ${sizeKB}KB`);
    
    if (size > performanceConfig.codeSplitting.chunkSize) {
      console.warn(`Large bundle detected: ${bundleName} (${sizeKB}KB)`);
      console.warn('Consider code splitting or lazy loading for this bundle.');
    }
  },
  
  // Get bundle recommendations
  getRecommendations: (bundleName: string, size: number) => {
    const recommendations = [];
    
    if (size > 244 * 1024) { // 244KB
      recommendations.push('Consider implementing code splitting');
      recommendations.push('Use lazy loading for non-critical components');
      recommendations.push('Optimize imports to reduce bundle size');
    }
    
    if (size > 500 * 1024) { // 500KB
      recommendations.push('Bundle is very large - consider aggressive optimization');
      recommendations.push('Review and remove unused dependencies');
    }
    
    return recommendations;
  },
};

// Performance optimization utilities
export const optimizationUtils = {
  // Check if performance monitoring is enabled
  isMonitoringEnabled: () => config.features.enablePerformanceMonitoring,
  
  // Check if memoization is enabled
  isMemoizationEnabled: () => config.performance.enableMemoization,
  
  // Check if code splitting is enabled
  isCodeSplittingEnabled: () => config.performance.enableCodeSplitting,
  
  // Get performance metrics
  getMetrics: () => ({
    codeSplitting: config.performance.enableCodeSplitting,
    lazyLoading: config.performance.enableLazyLoading,
    memoization: config.performance.enableMemoization,
    bundleAnalysis: config.performance.bundleAnalyzerEnabled,
  }),
};

// Create the performance utilities object
const performanceUtils = {
  performanceMonitoring,
  createCache,
  debounce,
  throttle,
  getPerformanceConfig,
  bundleAnalysis,
  optimizationUtils,
};

// Default export
export default performanceUtils; 